import { useState, useEffect } from 'react';
import { Globe, Type, Bell, CreditCard, Check, ExternalLink, Loader2 } from 'lucide-react';
import { useSubscriptionStatus, useSubscriptionStore } from '../stores/subscriptionStore';
import { subscriptionService } from '../api/services/subscription-service';
import { ThemeToggle } from './ThemeToggle';

function SettingsPage() {
  const { isActive } = useSubscriptionStatus();
  const [isPortalLoading, setIsPortalLoading] = useState(false);
  const { getPortalCache, setPortalCache } = useSubscriptionStore();

  const [settings, setSettings] = useState({
    language: 'pt-BR',
    fontSize: 'medium',
    notifications: {
      messages: true,
      updates: false,
      marketing: false,
    },
  });

  // Load portal session when subscription is active
  useEffect(() => {
    const loadPortalSession = async () => {
      if (!isActive) return;

      // Check cache first
      const cachedUrl = getPortalCache();
      if (cachedUrl) return;

      if (!isPortalLoading) {
        setIsPortalLoading(true);
        try {
          const session = await subscriptionService.createPortalSession();
          setPortalCache(session.portal_url);
        } catch (error) {
          console.error('Failed to create portal session:', error);
        } finally {
          setIsPortalLoading(false);
        }
      }
    };

    loadPortalSession();
  }, [isActive, isPortalLoading, getPortalCache, setPortalCache]);

  const handleLanguageChange = (language: string) => {
    setSettings({ ...settings, language });
  };

  const handleFontSizeChange = (fontSize: string) => {
    setSettings({ ...settings, fontSize });
  };

  const handleNotificationChange = (type: string, value: boolean) => {
    setSettings({
      ...settings,
      notifications: {
        ...settings.notifications,
        [type]: value,
      },
    });
  };

  const languages = [
    { code: 'pt-BR', name: 'Português (Brasil)' },
    { code: 'en-US', name: 'English (US)' },
    { code: 'es-ES', name: 'Español' },
    { code: 'fr-FR', name: 'Français' },
  ];

  const fontSizes = [
    { value: 'small', label: 'Small', description: 'Smaller text' },
    { value: 'medium', label: 'Medium', description: 'Default size' },
    { value: 'large', label: 'Large', description: 'Larger text' },
    { value: 'extra-large', label: 'Extra Large', description: 'Best for reading' },
  ];

  return (
    <div className="flex-1 bg-kind-deep p-4 lg:p-8 overflow-y-auto">
      <div className="max-w-4xl mx-auto">
        {/* Mobile Header */}
        <div className="lg:hidden mb-6">
          <h1 className="text-2xl font-title font-medium text-kind-text-primary">Settings</h1>
        </div>

        {/* Desktop Header */}
        <div className="hidden lg:block mb-8">
          <h1 className="text-4xl font-title font-medium text-kind-text-primary mb-2">Settings</h1>
          <p className="font-body font-light text-kind-text-secondary">
            Customize your OpenSelf experience
          </p>
        </div>

        <div className="space-y-6 lg:space-y-8">
          {/* Subscription Settings */}
          <div className="bg-kind-graphite rounded-2xl p-4 lg:p-8 border border-gray-300 dark:border-white/10">
            <div className="flex items-center space-x-3 mb-4 lg:mb-6">
              <CreditCard className="w-5 h-5 lg:w-6 lg:h-6 text-kind-purple" />
              <h2 className="text-xl lg:text-2xl font-t itle font-medium text-kind-text-primary">
                Subscription
              </h2>
            </div>

            <div className="space-y-4 lg:space-y-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between p-4 lg:p-6 bg-kind-container rounded-xl border border-gray-300 dark:border-white/10 space-y-4 lg:space-y-0">
                <div>
                  <h3 className="text-lg lg:text-xl font-medium text-kind-text-primary mb-2">
                    Current Plan
                  </h3>
                  <div className="flex items-center space-x-3">
                    <span className="px-2 lg:px-3 py-1 bg-kind-purple text-white text-xs lg:text-sm font-bold rounded-full">
                      Premium
                    </span>
                    <span
                      className={`text-xs lg:text-sm font-medium ${
                        isActive ? 'text-green-400' : 'text-red-400'
                      }`}
                    >
                      {isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                <div className="text-left lg:text-right">
                  <p className="text-xs lg:text-sm text-kind-text-secondary mb-1">Status</p>
                  <p className="text-base lg:text-lg font-medium text-kind-text-primary">
                    {isActive ? 'Subscription Active' : 'No Active Subscription'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 lg:gap-4">
                <button
                  onClick={() => {
                    const portalUrl = getPortalCache();
                    if (portalUrl) {
                      window.open(portalUrl, '_blank', 'noopener,noreferrer');
                    }
                  }}
                  disabled={isPortalLoading || !getPortalCache() || !isActive}
                  className={`p-3 lg:p-4 rounded-xl transition-colors duration-200 text-sm lg:text-base font-medium flex items-center justify-center space-x-2 ${
                    isPortalLoading || !getPortalCache() || !isActive
                      ? 'bg-kind-container/50 text-kind-text-secondary cursor-not-allowed opacity-50'
                      : 'bg-kind-purple text-white hover:bg-kind-purple/90'
                  }`}
                >
                  {isPortalLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <ExternalLink className="w-4 h-4" />
                  )}
                  <span>{isPortalLoading ? 'Loading...' : 'Manage Subscription'}</span>
                </button>
              </div>

              <div className="p-3 lg:p-4 bg-kind-teal/20 rounded-xl border border-kind-teal/30">
                <h4 className="text-base lg:text-lg font-medium text-kind-text-primary mb-2">
                  Premium Benefits
                </h4>
                <ul className="space-y-2 text-xs lg:text-sm text-kind-text-secondary">
                  <li className="flex items-center space-x-2">
                    <Check className="w-3 h-3 lg:w-4 lg:h-4 text-kind-teal" />
                    <span>Unlimited access to all companions</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <Check className="w-3 h-3 lg:w-4 lg:h-4 text-kind-teal" />
                    <span>Priority messaging</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <Check className="w-3 h-3 lg:w-4 lg:h-4 text-kind-teal" />
                    <span>Early access to new companions</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <Check className="w-3 h-3 lg:w-4 lg:h-4 text-kind-teal" />
                    <span>Priority support</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Appearance Settings */}
          <ThemeToggle />
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;
